import * as Dialog from "@radix-ui/react-dialog";
import { css, cx, recipe } from "dx-styles";
import type { ReactNode } from "react";

import { tokens } from "../theme/tokens";

export const button = recipe({
  base: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: tokens.space.sm,
    borderRadius: tokens.radius.sm,
    border: `1px solid ${tokens.color.rule}`,
    cursor: "pointer",
    fontWeight: 600,
    textDecoration: "none",
    transition: "background-color 120ms ease, border-color 120ms ease",
    "&:disabled": { cursor: "not-allowed", opacity: 0.5 },
  },
  variants: {
    tone: {
      primary: {
        backgroundColor: tokens.color.olive,
        borderColor: tokens.color.olive,
        color: tokens.color.paper,
        "&:hover:not(:disabled)": { backgroundColor: tokens.color.ink },
      },
      quiet: {
        backgroundColor: tokens.color.paper,
        color: tokens.color.ink,
        "&:hover:not(:disabled)": { backgroundColor: tokens.color.kraft },
      },
      danger: {
        backgroundColor: tokens.color.paper,
        borderColor: tokens.color.signal,
        color: tokens.color.signal,
        "&:hover:not(:disabled)": { backgroundColor: tokens.color.kraft },
      },
    },
    size: {
      sm: { minHeight: "28px", paddingInline: tokens.space.sm, fontSize: "13px" },
      md: { minHeight: "36px", paddingInline: tokens.space.md },
    },
  },
  defaultVariants: { tone: "quiet", size: "md" },
});

export const card = css({
  backgroundColor: tokens.color.paper,
  border: `1px solid ${tokens.color.rule}`,
  borderRadius: tokens.radius.md,
  padding: tokens.space.lg,
});

export const stack = css({ display: "flex", flexDirection: "column", gap: tokens.space.md });
export const row = css({ display: "flex", alignItems: "center", gap: tokens.space.sm });
export const spread = css({ display: "flex", alignItems: "center", justifyContent: "space-between", gap: tokens.space.md });
export const muted = css({ color: tokens.color.pencil });
export const mono = css({ fontFamily: tokens.font.mono, letterSpacing: "0.04em" });
export const heading = css({ fontSize: "22px", fontWeight: 700, margin: 0 });
export const subheading = css({ fontSize: "16px", fontWeight: 600, margin: 0 });
export const errorText = css({ color: tokens.color.signal });
export const srOnly = css({
  position: "absolute",
  width: "1px",
  height: "1px",
  padding: 0,
  margin: "-1px",
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
});

const overlay = css({
  position: "fixed",
  inset: 0,
  backgroundColor: "rgba(51, 48, 31, 0.4)",
});

const content = css({
  position: "fixed",
  top: "50%",
  insetInlineStart: "50%",
  transform: "translate(-50%, -50%)",
  width: "min(520px, calc(100vw - 32px))",
  maxHeight: "85vh",
  overflowY: "auto",
  backgroundColor: tokens.color.paper,
  border: `1px solid ${tokens.color.rule}`,
  borderRadius: tokens.radius.lg,
  padding: tokens.space.lg,
  display: "flex",
  flexDirection: "column",
  gap: tokens.space.md,
});

const dialogTitle = css({ fontSize: "18px", fontWeight: 700, margin: 0 });

/**
 * Radix does the keyboard and focus work; this only dresses it. Every dialog in
 * the app goes through here so escape, focus return and the labelled title
 * behave the same everywhere.
 */
export function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  readonly description?: string | undefined;
  readonly children: ReactNode;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={overlay} />
        <Dialog.Content className={content}>
          <Dialog.Title className={dialogTitle}>{title}</Dialog.Title>
          {description ? (
            <Dialog.Description className={muted}>{description}</Dialog.Description>
          ) : (
            <Dialog.Description className={srOnly}>{title}</Dialog.Description>
          )}
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function CopyButton({ value, label = "Copy" }: { readonly value: string; readonly label?: string }) {
  return (
    <button
      type="button"
      className={button({ size: "sm" })}
      onClick={() => {
        void navigator.clipboard?.writeText(value);
      }}
    >
      {label}
    </button>
  );
}

const waypoint = css({ display: "flex", alignItems: "center", gap: tokens.space.sm });
const waypointRing = css({
  width: "10px",
  height: "10px",
  border: `2px solid ${tokens.color.olive}`,
  borderRadius: "50%",
  flex: "none",
});
const waypointLabel = css({
  fontSize: "11px",
  fontWeight: 600,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: tokens.color.pencil,
  whiteSpace: "nowrap",
});
const waypointRule = css({ flex: 1, borderTop: `1px dashed ${tokens.color.rule}` });

/**
 * The section header from the artboards: a survey waypoint, its label, and a
 * dashed sight line to the edge. The count rides along because every section
 * that uses one is a list of something.
 */
export function Waypoint({ label, count }: { readonly label: string; readonly count?: number | undefined }) {
  return (
    <div className={waypoint}>
      <span className={waypointRing} aria-hidden="true" />
      <h2 className={waypointLabel}>
        {label}
        {count === undefined ? null : ` ${count}`}
      </h2>
      <span className={waypointRule} aria-hidden="true" />
    </div>
  );
}

export const layout = css({
  maxWidth: "1100px",
  margin: "0 auto",
  padding: tokens.space.lg,
  display: "flex",
  flexDirection: "column",
  gap: tokens.space.lg,
});

export const chrome = css({
  borderBottom: `1px solid ${tokens.color.rule}`,
  backgroundColor: tokens.color.paper,
});

export const brand = css({
  fontWeight: 700,
  letterSpacing: "0.02em",
  textDecoration: "none",
  color: tokens.color.ink,
});

export function classes(...parts: (string | false | null | undefined)[]): string {
  return cx(...parts);
}
